/**
 * StockScanner - Mobile-friendly stock/inventory scanning component
 * Scan items to record stock IN (returns, restocking) and stock OUT (used, damaged)
 */
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { stockScanAPI, financeAPI, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package, Scan, Search, Minus, Plus, RotateCcw, Truck,
  Loader2, AlertTriangle, Check, Box, ArrowDownToLine, ArrowUpFromLine,
  PackagePlus, PackageMinus, History, X
} from 'lucide-react';

const StockScanner = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [scanInput, setScanInput] = useState('');
  const [scannedItem, setScannedItem] = useState(null);
  const [stockMode, setStockMode] = useState('out'); // 'in' or 'out'
  const [transactionType, setTransactionType] = useState('used');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [recentTransactions, setRecentTransactions] = useState([]);
  const inputRef = useRef(null);

  // Fetch stock items for quick selection
  const { data: stockItems = [] } = useQuery({
    queryKey: ['finance-stock', eventId],
    queryFn: () => financeAPI.listStock(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Lookup mutation
  const lookupMutation = useMutation({
    mutationFn: (barcode) => stockScanAPI.lookup(eventId, barcode),
    onSuccess: (response) => {
      if (response.data.found) {
        setScannedItem(response.data.item);
        toast.success(`Found: ${response.data.item.name}`);
      } else {
        toast.error(response.data.message);
        setScannedItem(null);
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Lookup failed'));
      setScannedItem(null);
    },
  });

  // Scan transaction mutation - Updated for new API format
  const scanMutation = useMutation({
    mutationFn: (data) => stockScanAPI.scan(eventId, data),
    onSuccess: (response) => {
      const data = response.data;
      toast.success(data.message);
      
      // Update scanned item with new quantity
      if (data.item) {
        setScannedItem({
          ...data.item,
          quantity: data.new_quantity
        });
      }
      
      // Add to recent transactions
      setRecentTransactions(prev => [{
        item: data.item?.name || scannedItem?.name,
        type: transactionType,
        direction: stockMode,
        quantity: quantity,
        previous_quantity: data.previous_quantity,
        new_quantity: data.new_quantity,
        timestamp: new Date(),
      }, ...prev].slice(0, 10));
      
      if (data.low_stock_warning) {
        toast.warning('Low stock warning!', {
          description: `Only ${data.new_quantity} remaining (min: ${data.min_quantity})`,
        });
      }
      
      queryClient.invalidateQueries(['finance-stock', eventId]);
      setQuantity(1);
      setNotes('');
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Transaction failed'));
    },
  });

  // Handle lookup
  const handleLookup = () => {
    if (!scanInput.trim()) {
      toast.error('Enter barcode or item name');
      return;
    }
    lookupMutation.mutate(scanInput.trim());
  };

  // Handle transaction - Updated for new API format
  const handleTransaction = () => {
    if (!scannedItem) {
      toast.error('Select or scan an item first');
      return;
    }
    if (quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    // Map transaction types to reasons
    const reasonMap = {
      'used': 'used',
      'returned': 'returned',
      'restocked': 'received',
      'damaged': 'damaged',
      'expired': 'expired',
      'adjustment': 'adjustment'
    };

    scanMutation.mutate({
      item_id: scannedItem.id,
      direction: stockMode,  // 'in' or 'out'
      reason: reasonMap[transactionType] || transactionType,
      quantity,
      notes,
    });
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLookup();
    }
  };

  // Select item from list
  const handleSelectItem = (item) => {
    setScannedItem(item);
    setScanInput('');
  };

  // Update transaction type when mode changes
  useEffect(() => {
    if (stockMode === 'out') {
      setTransactionType('used');
    } else {
      setTransactionType('returned');
    }
  }, [stockMode]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'used': return <PackageMinus className="h-4 w-4" />;
      case 'returned': return <RotateCcw className="h-4 w-4" />;
      case 'restocked': return <Truck className="h-4 w-4" />;
      case 'damaged': return <AlertTriangle className="h-4 w-4" />;
      default: return <Box className="h-4 w-4" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'used': return 'text-red-600';
      case 'returned': return 'text-green-600';
      case 'restocked': return 'text-blue-600';
      case 'damaged': return 'text-amber-600';
      default: return 'text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="text-center pb-2">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
            <Package className="h-6 w-6" />
            Stock Scanner
          </h1>
          <p className="text-slate-500 text-sm">Track inventory in and out</p>
        </div>

        {/* Stock In/Out Mode Toggle */}
        <Card className="overflow-hidden">
          <Tabs value={stockMode} onValueChange={setStockMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-14 rounded-none">
              <TabsTrigger 
                value="out" 
                className="h-full text-base data-[state=active]:bg-red-500 data-[state=active]:text-white"
                data-testid="stock-out-tab"
              >
                <ArrowUpFromLine className="h-5 w-5 mr-2" />
                Stock OUT
              </TabsTrigger>
              <TabsTrigger 
                value="in" 
                className="h-full text-base data-[state=active]:bg-green-500 data-[state=active]:text-white"
                data-testid="stock-in-tab"
              >
                <ArrowDownToLine className="h-5 w-5 mr-2" />
                Stock IN
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </Card>

        {/* Scan Input */}
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  ref={inputRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Scan barcode or search..."
                  className="pl-10 h-14 text-lg"
                  data-testid="stock-scan-input"
                />
              </div>
              <Button
                onClick={handleLookup}
                disabled={!scanInput || lookupMutation.isPending}
                className="h-14 px-6"
                data-testid="stock-lookup-btn"
              >
                {lookupMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Scan className="h-5 w-5" />
                )}
              </Button>
            </div>

            {/* Quick Select from existing items */}
            {!scannedItem && stockItems.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-slate-500 mb-2">Or select an item:</p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {stockItems.slice(0, 10).map((item) => (
                    <Badge
                      key={item.id}
                      variant="outline"
                      className="cursor-pointer hover:bg-slate-100 py-1.5 px-3"
                      onClick={() => handleSelectItem(item)}
                    >
                      {item.name}
                      <span className="ml-1 text-slate-400">({item.quantity_available})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Item */}
        {scannedItem && (
          <Card className={`border-2 ${stockMode === 'out' ? 'border-red-500' : 'border-green-500'}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{scannedItem.name}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant={scannedItem.quantity_available <= scannedItem.reorder_level ? 'destructive' : 'secondary'}>
                    {scannedItem.quantity_available} in stock
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={() => {
                      setScannedItem(null);
                      setScanInput('');
                      inputRef.current?.focus();
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {scannedItem.category && (
                <CardDescription>{scannedItem.category}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Stock Level */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Stock Level</span>
                  <span>{scannedItem.quantity_available} / {scannedItem.quantity_purchased}</span>
                </div>
                <Progress 
                  value={(scannedItem.quantity_available / scannedItem.quantity_purchased) * 100} 
                  className="h-2"
                />
              </div>

              {/* Transaction Type - Based on Mode */}
              {stockMode === 'out' ? (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={transactionType === 'used' ? 'default' : 'outline'}
                    className={`flex-col h-auto py-3 ${transactionType === 'used' ? 'bg-red-500 hover:bg-red-600' : ''}`}
                    onClick={() => setTransactionType('used')}
                    data-testid="type-used"
                  >
                    <PackageMinus className="h-5 w-5" />
                    <span className="text-sm mt-1">Used / Given Out</span>
                  </Button>
                  <Button
                    variant={transactionType === 'damaged' ? 'default' : 'outline'}
                    className={`flex-col h-auto py-3 ${transactionType === 'damaged' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                    onClick={() => setTransactionType('damaged')}
                    data-testid="type-damaged"
                  >
                    <AlertTriangle className="h-5 w-5" />
                    <span className="text-sm mt-1">Damaged / Lost</span>
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={transactionType === 'returned' ? 'default' : 'outline'}
                    className={`flex-col h-auto py-3 ${transactionType === 'returned' ? 'bg-green-500 hover:bg-green-600' : ''}`}
                    onClick={() => setTransactionType('returned')}
                    data-testid="type-returned"
                  >
                    <RotateCcw className="h-5 w-5" />
                    <span className="text-sm mt-1">Returned</span>
                  </Button>
                  <Button
                    variant={transactionType === 'restocked' ? 'default' : 'outline'}
                    className={`flex-col h-auto py-3 ${transactionType === 'restocked' ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
                    onClick={() => setTransactionType('restocked')}
                    data-testid="type-restocked"
                  >
                    <Truck className="h-5 w-5" />
                    <span className="text-sm mt-1">New Stock</span>
                  </Button>
                </div>
              )}

              {/* Quantity */}
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="h-12 w-12"
                >
                  <Minus className="h-5 w-5" />
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-12 text-center text-2xl font-bold flex-1"
                  min={1}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  className="h-12 w-12"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>

              {/* Quick quantity buttons */}
              <div className="flex gap-2 justify-center">
                {[1, 5, 10, 25].map(num => (
                  <Button
                    key={num}
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(num)}
                    className={quantity === num ? 'border-2 border-slate-400' : ''}
                  >
                    {num}
                  </Button>
                ))}
              </div>

              {/* Notes (optional) */}
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="h-12"
              />

              {/* Submit Button */}
              <Button
                onClick={handleTransaction}
                disabled={scanMutation.isPending}
                className={`w-full h-14 text-lg ${
                  stockMode === 'out' 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-green-500 hover:bg-green-600'
                }`}
                data-testid="stock-submit-btn"
              >
                {scanMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : stockMode === 'out' ? (
                  <ArrowUpFromLine className="h-5 w-5 mr-2" />
                ) : (
                  <ArrowDownToLine className="h-5 w-5 mr-2" />
                )}
                <span>
                  {stockMode === 'out' ? (
                    transactionType === 'used' 
                      ? `Take Out ${quantity}` 
                      : `Mark ${quantity} Damaged`
                  ) : (
                    transactionType === 'returned'
                      ? `Return ${quantity}`
                      : `Add ${quantity} New Stock`
                  )}
                </span>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent Transactions */}
        {recentTransactions.length > 0 && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <History className="h-4 w-4" />
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent className="py-0 pb-3">
              <div className="space-y-2">
                {recentTransactions.map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={getTransactionColor(tx.type)}>
                        {getTransactionIcon(tx.type)}
                      </span>
                      <span>{tx.item}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getTransactionColor(tx.type)}>
                        {tx.type === 'used' || tx.type === 'damaged' ? '-' : '+'}{tx.quantity}
                      </Badge>
                      <span className="text-xs text-slate-400">
                        {tx.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low Stock Alerts */}
        {stockItems.filter(i => i.quantity_available <= i.reorder_level).length > 0 && (
          <Alert className="border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              <strong>Low Stock Items:</strong>
              <div className="flex flex-wrap gap-1 mt-1">
                {stockItems
                  .filter(i => i.quantity_available <= i.reorder_level)
                  .slice(0, 5)
                  .map(item => (
                    <Badge 
                      key={item.id} 
                      variant="outline" 
                      className="text-amber-700 border-amber-400 cursor-pointer hover:bg-amber-100"
                      onClick={() => handleSelectItem(item)}
                    >
                      {item.name}: {item.quantity_available}
                    </Badge>
                  ))
                }
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Instructions */}
        <Alert>
          <Package className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <strong>Quick Guide:</strong>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <p className="font-medium text-red-600 flex items-center gap-1">
                  <ArrowUpFromLine className="h-3 w-3" /> Stock OUT
                </p>
                <ul className="list-disc ml-4 text-xs space-y-0.5">
                  <li>Used/Given Out</li>
                  <li>Damaged/Lost</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-green-600 flex items-center gap-1">
                  <ArrowDownToLine className="h-3 w-3" /> Stock IN
                </p>
                <ul className="list-disc ml-4 text-xs space-y-0.5">
                  <li>Returned items</li>
                  <li>New stock received</li>
                </ul>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default StockScanner;
